import { useEffect, useRef, useState } from 'react'
import { listPageMarks } from '../../services/pageMarks'
import './PageMarks.css'

const POS_KEY = 'ct-page-mark-pos'
// Let a scroll settle before asking which comments and guides cover the page.
const PAGE_SETTLE_MS = 400

function loadPos() {
  try {
    const raw = JSON.parse(localStorage.getItem(POS_KEY) || '{}')
    return {
      c: validPoint(raw.c) ? raw.c : null,
      g: validPoint(raw.g) ? raw.g : null,
    }
  } catch {
    return { c: null, g: null }
  }
}

function validPoint(p) {
  return p && Number.isFinite(p.x) && Number.isFinite(p.y)
}

function snippet(text, max = 140) {
  const flat = String(text || '').replace(/\s+/g, ' ').trim()
  if (flat.length <= max) return flat
  return `${flat.slice(0, max - 1).trimEnd()}…`
}

function defaultPoint(kind, width) {
  return { x: Math.max(8, width - 40), y: kind === 'c' ? 16 : 48 }
}

function clamp(point, width, height) {
  return {
    x: Math.max(4, Math.min(point.x, Math.max(4, width - 26))),
    y: Math.max(4, Math.min(point.y, Math.max(4, height - 26))),
  }
}

// One draggable C and one draggable G for the page currently on screen.
// Nothing is drawn when that page has no comments or guides the user can see.
export function PageMarks({ standard, page, onOpenComment, onOpenGuide }) {
  const rootRef = useRef(null)
  const dragRef = useRef(null)
  const [box, setBox] = useState({ w: 0, h: 0 })
  const [saved, setSaved] = useState(loadPos)
  const [marks, setMarks] = useState(null)
  const [open, setOpen] = useState(null)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return undefined
    const measure = () => setBox({ w: el.clientWidth, h: el.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setMarks(null)
    setOpen(null)
    if (!standard || !page) return undefined
    let alive = true
    const timer = setTimeout(() => {
      listPageMarks(standard, page)
        .then((data) => {
          if (!alive) return
          setMarks({
            comments: data.comments || [],
            guides: data.guides || [],
          })
        })
        .catch(() => {
          if (alive) setMarks({ comments: [], guides: [] })
        })
    }, PAGE_SETTLE_MS)
    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [standard, page])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(null)
    }
    const onDown = (event) => {
      if (rootRef.current?.contains(event.target)) return
      setOpen(null)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onDown)
    }
  }, [open])

  const place = (kind) => {
    const point = saved[kind] || defaultPoint(kind, box.w)
    return clamp(point, box.w, box.h)
  }

  const onPointerDown = (event, kind) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    const point = place(kind)
    dragRef.current = {
      kind,
      x: event.clientX,
      y: event.clientY,
      ox: point.x,
      oy: point.y,
      moved: false,
    }
  }

  const onPointerMove = (event) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = event.clientX - drag.x
    const dy = event.clientY - drag.y
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) drag.moved = true
    const point = clamp({ x: drag.ox + dx, y: drag.oy + dy }, box.w, box.h)
    drag.point = point
    setSaved((prev) => ({ ...prev, [drag.kind]: point }))
  }

  const onPointerUp = () => {
    const drag = dragRef.current
    dragRef.current = null
    if (!drag) return
    if (drag.moved && drag.point) {
      setSaved((prev) => {
        const next = { ...prev, [drag.kind]: drag.point }
        try {
          localStorage.setItem(POS_KEY, JSON.stringify(next))
        } catch {
          /* private mode or full storage — the drag still holds this session */
        }
        return next
      })
      setOpen(null)
      return
    }
    setOpen((current) => (current === drag.kind ? null : drag.kind))
  }

  const menuStyle = (point) => {
    const width = 280
    const left = Math.max(8, Math.min(point.x, Math.max(8, box.w - width - 8)))
    const below = point.y + 28
    const spaceBelow = box.h - below
    if (spaceBelow < 140 && point.y > spaceBelow) {
      return {
        left,
        bottom: Math.max(8, box.h - point.y + 6),
        maxHeight: Math.max(120, Math.min(280, point.y - 12)),
      }
    }
    return {
      left,
      top: below,
      maxHeight: Math.max(120, Math.min(280, spaceBelow - 8)),
    }
  }

  const comments = marks?.comments || []
  const guides = marks?.guides || []
  if (!box.w || (!comments.length && !guides.length)) {
    return <div className="page-marks" ref={rootRef} />
  }

  const commentPoint = comments.length ? place('c') : null
  const guidePoint = guides.length ? place('g') : null

  return (
    <div className="page-marks" ref={rootRef}>
      {commentPoint ? (
        <Mark
          letter="C"
          label="Comments on this page"
          point={commentPoint}
          open={open === 'c'}
          menuStyle={menuStyle(commentPoint)}
          onPointerDown={(event) => onPointerDown(event, 'c')}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          {comments.map((comment) => (
            <button
              key={comment._id}
              type="button"
              className="page-mark-option"
              onClick={() => {
                setOpen(null)
                onOpenComment?.(comment._id)
              }}
            >
              {comment.title ? <span className="page-mark-title">{comment.title}</span> : null}
              <span className="page-mark-text">{snippet(comment.text) || 'Comment'}</span>
              <span className="page-mark-meta">
                {[comment.authorName, comment.itemLabels?.join(', ')].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}
        </Mark>
      ) : null}
      {guidePoint ? (
        <Mark
          letter="G"
          label="Guides on this page"
          point={guidePoint}
          open={open === 'g'}
          menuStyle={menuStyle(guidePoint)}
          onPointerDown={(event) => onPointerDown(event, 'g')}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          {guides.map((guide) => (
            <button
              key={guide._id}
              type="button"
              className="page-mark-option"
              onClick={() => {
                setOpen(null)
                onOpenGuide?.(guide._id)
              }}
            >
              <span className="page-mark-title">{guide.title || 'Untitled guide'}</span>
              {guide.description ? (
                <span className="page-mark-text">{snippet(guide.description, 100)}</span>
              ) : null}
            </button>
          ))}
        </Mark>
      ) : null}
    </div>
  )
}

function Mark({
  letter,
  label,
  point,
  open,
  menuStyle,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  children,
}) {
  return (
    <>
      <button
        type="button"
        className={'page-mark' + (open ? ' is-open' : '')}
        style={{ left: point.x, top: point.y }}
        title={label}
        aria-label={label}
        aria-expanded={open}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {letter}
      </button>
      {open ? (
        <div className="page-mark-menu" style={menuStyle} role="menu">
          {children}
        </div>
      ) : null}
    </>
  )
}

export default PageMarks
